"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { FEATURES } from "@/lib/constants";
import { AnimateOnScroll } from "@/components/shared/AnimateOnScroll";

export function Features() {
  return (
    <section id="features" className="scroll-mt-16 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <AnimateOnScroll>
          <h2 className="text-center text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Everything your brain needs
          </h2>
        </AnimateOnScroll>
        <AnimateOnScroll delay={0.15}>
          <p className="mx-auto mt-5 max-w-2xl text-center text-xl text-muted-foreground sm:text-2xl">
            A coach that plans each day with you, plus habits, nutrition and
            cognitive tests in one app.
          </p>
        </AnimateOnScroll>

        <div className="mt-16 flex flex-col gap-12 sm:mt-24 sm:gap-16">
          {FEATURES.map((feature, i) => {
            const imageOnLeft = i % 2 === 0;
            // Alternate gradient tints for visual variety
            const gradients = [
              "from-[hsl(217_91%_60%/0.06)] via-[hsl(217_80%_72%/0.04)] to-transparent",
              "from-[hsl(142_71%_45%/0.06)] via-[hsl(217_91%_60%/0.03)] to-transparent",
              "from-[hsl(25_95%_53%/0.06)] via-[hsl(217_91%_60%/0.03)] to-transparent",
            ];

            return (
              <div
                key={feature.title}
                className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${gradients[i % gradients.length]} border border-border/50 p-8 sm:p-12`}
              >
                <div
                  className={`flex flex-col items-center gap-10 lg:gap-16 ${
                    imageOnLeft ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  {/* Screenshot — anchored to its OUTER card edge on lg, so
                      the card padding is the shared grid line for every row. */}
                  <AnimateOnScroll
                    direction={imageOnLeft ? "left" : "right"}
                    className={`relative flex flex-1 justify-center ${
                      imageOnLeft ? "lg:justify-start" : "lg:justify-end"
                    }`}
                  >
                    <div
                      className="relative w-[280px] sm:w-[320px] lg:w-[380px]"
                    >
                      {/* Glow inside the phone wrapper so it tracks the phone
                          wherever the column anchors it */}
                      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />
                      <Image
                        src={feature.screenshot}
                        alt={feature.alt}
                        width={480}
                        height={960}
                        sizes="(min-width: 1024px) 380px, (min-width: 640px) 320px, 280px"
                        className="relative drop-shadow-xl"
                      />
                    </div>
                  </AnimateOnScroll>

                  {/* Text — anchored to ITS outer card edge (mirroring the
                      phone) and extending inward toward the gap: text-left
                      rows start on the same grid line the phone-left rows
                      start on; text-right rows end where phone-right rows
                      end. Wider block uses the middle whitespace. */}
                  <AnimateOnScroll
                    delay={0.15}
                    className={`flex flex-1 flex-col items-center ${
                      imageOnLeft ? "lg:items-end" : "lg:items-start"
                    }`}
                  >
                    <div className="max-w-md text-center lg:w-full lg:max-w-lg lg:text-left">
                      <h3 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                        {feature.title}
                      </h3>
                      <p className="mt-4 text-lg leading-relaxed text-muted-foreground sm:text-xl">
                        {feature.description}
                      </p>
                    </div>
                  </AnimateOnScroll>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
